import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { LoadMeeting, GetRoomStateEntity, LoadUserMeetingSettings } from '../connection/meeting';
import { getCurrentShotIdEntity } from '../connection/shots';
import { RequestSyncedState } from '../connection/sync';
import { getBroadcastServer } from '../stores/actions/broadcasting';
import {
	getModeratedComments,
	getPresenterMessages,
	loadChatPosts,
	loadRoomChat,
} from '../stores/actions/chat';
import { getDefaultBackgrounds } from '../stores/actions/content';
import { loadGraphicsAction } from '../stores/actions/graphics';
import { loadNotifications } from '../stores/actions/home';
import {
	getInviteSsoDomains,
	getPendingJoinRequests,
	loadInvitations,
} from '../stores/actions/invitations';
import {
	getMedia,
	getMediaPagesForMeeting,
} from '../stores/actions/media';
import {
	getProfile,
	getShowSessions,
	getShowSessionTypes,
	setUserMeetingSettings,
} from '../stores/actions/session';
import { replaceState } from '../stores/actions/settings';
import { loadShots, loadSingleShotAsShots } from '../stores/actions/shots';
import type { Meeting, ShowroomsTokens } from '../stores/types/session';
import { APP } from '../stores/session-status';
import { GreenroomState as GS } from '../stores/types/types';
import { getStorageItem } from '../utils/local-storage';
import { getStateFromSettingsJson } from '../utils/sync';
import {
	getAllAnimatedOverlays,
	getAllShotOverlays,
} from '../connection/graphics';
import { GetScheduledStream } from '../connection/scheduled-streams';
import { setScheduledStream } from '../stores/actions/meeting';
import { GetDestinationsForMeeting, GetSpielbergBroadcast } from '../connection/broadcasting';
import store from '../stores/main';
import { GetOpentokSessionEntity } from '../connection/opentok';
import { loadMasks } from '../stores/actions/masks';
import { getOnsiteLocations, getSrtStreams } from '../stores/actions/video';
import { GetRecentSlideDeck } from '../connection/media';
import { loadDefaultThemes, loadThemeMaps } from '../stores/actions/themes';
import { loadFontAssets } from '../stores/actions/font-assets';

interface LoadMeetingRoomParams {
	passedInToken: string;
	passedInMeeting: Meeting;
	embed: boolean;
	isReload: boolean;
	showroomsTokens?: ShowroomsTokens;
	successCallback?: () => void;
}

const useLoadCustomHook = () => {
	const dispatch = useDispatch();
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const appSignedInWith = useSelector((s: GS) => s.SessionReducer.appSignedInWith);
	const showSessionTypes = useSelector((s: GS) => s.SessionReducer.showSessionTypes);
	const homeMeeting = useSelector((s: GS) => s.HomeReducer?.homeMeeting);
	const useMasks = useSelector((s: GS) => s.SettingsReducer?.featureFlags?.use_profile_masks);
	const useStylepacks = useSelector((s: GS) => s.SettingsReducer?.featureFlags?.use_title_library);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);

	// Load Beehive scheduled stream data
	const loadScheduledStream = async (
		passedInToken: string,
		passedInMeetingId: number,
	) => {
		const scheduledStream = await GetScheduledStream(
			passedInToken,
			passedInMeetingId,
		);

		dispatch(setScheduledStream(scheduledStream));
	};

	const loadMeetingRoom = async ({
		passedInToken,
		passedInMeeting,
		embed,
		isReload,
		showroomsTokens,
		successCallback = () => { },
	}: LoadMeetingRoomParams): Promise<void> => {
		if (passedInMeeting) {
			/*
				It may seem overly complex to have both `userOnlyToken` and
				`embedOrUserToken`, but that makes it easier to see what is
				happening below since only certain routes will accept the embed
				token and this enables us to label which routes should be using
				which token for clarity.
			*/
			const userOnlyToken: string | null = !embed
				? passedInToken
				: null;
			const embedOrUserToken = passedInToken;

			let upToDateMeeting = passedInMeeting;

			if (isReload && userOnlyToken) {
				upToDateMeeting = await LoadMeeting(userOnlyToken, passedInMeeting.meeting);
			}

			if (upToDateMeeting) {
				const useSpielberg = store.getState()?.SettingsReducer?.featureFlags?.use_spielberg;
				const enablerShotId = store.getState()?.MeetingReducer?.enablerShotId;
				const isEnablerView = embed && !!enablerShotId;

				const { meeting: meetingRowId } = upToDateMeeting;
				// Load Beehive data
				RequestSyncedState(meetingRowId, embedOrUserToken);
				dispatch(getSrtStreams(embedOrUserToken, meetingRowId));
				dispatch(getOnsiteLocations(embedOrUserToken, meetingRowId));

				// Fetch all sync/Beehive state in parallel — none of these depend on each other
				const [
					opentokSessionEntity,
					destinationsEntity,
					roomStateEntity,
					currentShotIdEntity,
					broadcastEntity,
					recentSlideDeckEntity,
					allShotOverlaysEntity,
					allAnimatedOverlaysEntity,
				] = await Promise.all([
					!isEnablerView ? GetOpentokSessionEntity(embedOrUserToken, meetingRowId) : null,
					!isEnablerView ? GetDestinationsForMeeting(embedOrUserToken, meetingRowId) : null,
					!isEnablerView ? GetRoomStateEntity(embedOrUserToken, meetingRowId) : null,
					!isEnablerView ? getCurrentShotIdEntity(embedOrUserToken, meetingRowId) : null,
					GetSpielbergBroadcast(embedOrUserToken, meetingRowId),
					GetRecentSlideDeck(meetingRowId),
					!isEnablerView ? getAllShotOverlays(embedOrUserToken, meetingRowId) : null,
					!isEnablerView ? getAllAnimatedOverlays(embedOrUserToken, meetingRowId) : null,
				]);

				if (opentokSessionEntity) dispatch({ type: opentokSessionEntity.entityType, payload: opentokSessionEntity });
				if (destinationsEntity) dispatch({ type: destinationsEntity.entityType, payload: destinationsEntity });
				if (roomStateEntity) dispatch({ type: roomStateEntity.entityType, payload: roomStateEntity });
				if (currentShotIdEntity) dispatch({ type: currentShotIdEntity.entityType, payload: currentShotIdEntity });
				if (broadcastEntity) dispatch({ type: broadcastEntity.entityType, payload: broadcastEntity });
				if (recentSlideDeckEntity) dispatch({ type: recentSlideDeckEntity.entityType, payload: recentSlideDeckEntity });
				if (allShotOverlaysEntity) dispatch({ type: allShotOverlaysEntity.entityType, payload: allShotOverlaysEntity });
				if (allAnimatedOverlaysEntity) dispatch({ type: allAnimatedOverlaysEntity.entityType, payload: allAnimatedOverlaysEntity });

				loadScheduledStream(embedOrUserToken, meetingRowId);

				// Load API data
				// the routes below have been set up to manually accept the embed token
				if (isEnablerView && enablerShotId) {
					dispatch(loadSingleShotAsShots(embedOrUserToken, meetingRowId, enablerShotId));
				} else {
					dispatch(loadShots(embedOrUserToken, meetingRowId));
				}
				dispatch(getMedia(embedOrUserToken, meetingRowId));
				dispatch(getMediaPagesForMeeting(embedOrUserToken, meetingRowId));
				dispatch(loadInvitations(embedOrUserToken, meetingRowId));
				if (!embed)
					dispatch(loadDefaultThemes());
				dispatch(loadThemeMaps(upToDateMeeting.channel));
				dispatch(loadGraphicsAction(embedOrUserToken, meetingRowId));

				if (useStylepacks) {
					dispatch(loadFontAssets(meetingRowId));
				}

				const userMeetingSettings = await LoadUserMeetingSettings(embedOrUserToken, meetingRowId);
				if (userMeetingSettings) {
					dispatch(setUserMeetingSettings(userMeetingSettings));
				}
				
				if (useMasks) {
					dispatch(loadMasks(meetingRowId));
				}
				// the routes below have NOT been set up to accept the embed token
				// (the embed view does not need the data loaded from these routes)
				if (!embed && userOnlyToken) {
					dispatch(getShowSessions(userOnlyToken, meetingRowId));

					// Static items that are never changed from initial load
					if (!isReload) {
						dispatch(getDefaultBackgrounds(userOnlyToken));
					}
				}

				/*
					API data that only needs to be retrieved when reconnecting to socket.
					-
					The idea behind adding the `isReload` check is this `loadMeetingRoom`
					method is largely copying functionality that used to happen in the
					`meeting-room.tsx` component, yet when reconnecting to Socket, we
					also needs to reload items like chats that are set up in different
					components like `chat-main.tsx`.
				*/
				if (isReload && userOnlyToken) {
					const appSignedInWithStorage = getStorageItem('app-signed-in-with');

					if (appSignedInWithStorage) {
						dispatch(getProfile(
							userOnlyToken,
							appSignedInWithStorage,
							showroomsTokens?.accessToken,
						));
					}

					// don't start a Bullman server if the Spielberg flag is on when first joining a meeting
					if (!useSpielberg) {
						dispatch(getBroadcastServer(userOnlyToken, meetingRowId));
					}

					dispatch(loadNotifications(userOnlyToken));
					dispatch(loadChatPosts(userOnlyToken, meetingRowId));
					dispatch(loadRoomChat(userOnlyToken, meetingRowId));
					dispatch(getPendingJoinRequests(userOnlyToken, meetingRowId));

					// Replace state with `upToDateMeeting`
					dispatch(replaceState(
						getStateFromSettingsJson(upToDateMeeting.settings_json),
					));
				}

				successCallback();
			}
		}
	};

	const loadShowDetails = useCallback(async (): Promise<void> => {
		const homeMeetingId = homeMeeting?.meeting;

		if (token && homeMeetingId) {
			dispatch(loadInvitations(token, homeMeetingId));
			dispatch(getShowSessions(token, homeMeetingId));

			if (!showSessionTypes || showSessionTypes.length === 0) {
				dispatch(getShowSessionTypes(token));
			}

			dispatch(loadShots(token, homeMeetingId));
			dispatch(getMedia(token, homeMeetingId));
			dispatch(replaceState(JSON.parse(homeMeeting.settings_json)));
			dispatch(loadGraphicsAction(token, homeMeetingId));
			dispatch(getSrtStreams(token, homeMeetingId));
			dispatch(getOnsiteLocations(token, homeMeetingId));

			const destinationsEntity = await GetDestinationsForMeeting(token, homeMeetingId);
			if (destinationsEntity) {
				dispatch({ type: destinationsEntity.entityType, payload: destinationsEntity });
			}

			const broadcastEntity = await GetSpielbergBroadcast(token, homeMeetingId);
			if (broadcastEntity) {
				dispatch({ type: broadcastEntity.entityType, payload: broadcastEntity });
			}

			if (host) {
				// don't start a Bullman server if the Spielberg flag is on when first joining a meeting
				const useSpielberg = store.getState().SettingsReducer.featureFlags.use_spielberg;
				if (!useSpielberg) {
					dispatch(getBroadcastServer(token, homeMeetingId));
				}

				if (appSignedInWith === APP.EVENTS) {
					dispatch(getInviteSsoDomains(token, homeMeetingId));
				}

				loadScheduledStream(token, homeMeetingId);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [appSignedInWith, homeMeeting, host, showSessionTypes, token]);

	const loadLegacyModeratedChats = useCallback((): void => {
		if (token && meeting?.brandlive_event) {
			dispatch(getModeratedComments(token, meeting.brandlive_event));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meeting, token]);

	const loadModeratedChats = useCallback((): void => {
		if (token && meeting?.meeting_id && !embed) {
			dispatch(getPresenterMessages(token, meeting.meeting_id));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meeting, token, embed]);

	return {
		loadMeetingRoom,
		loadShowDetails,
		loadLegacyModeratedChats,
		loadModeratedChats,
	};
};

export default useLoadCustomHook;
